import * as React from 'react';
import { useCallback, forwardRef, useMemo } from 'react';
import * as ReactDOM from 'react-dom';
import MaskedInput from 'react-text-mask';

import TextField, { TextFieldProps } from '../text-field';
import { detectIsPhone } from '../../utils/validation';
import { withFormTextField } from '../form';
import { ValidationRule, getValueByName } from '../form/shared';
import { Root } from './styled';


export type PhoneNumberTextFieldProps = {
	appearance: 'default' | 'mobile-mask';
	enableOnChangeValidation?: boolean;
} & TextFieldProps;

const TextMask = (props) => {
	const { inputRef, ...rest } = props;
	const setRef = useCallback(ref => inputRef(ReactDOM.findDOMNode(ref)), []);

  return (
    <MaskedInput
      {...rest}
      ref={setRef}
      mask={['+', '7',' ','(',/\d/,/\d/,/\d/,')',' ',/\d/,/\d/,/\d/,'-',/\d/,/\d/,'-',/\d/,/\d/]}
      keepCharPositions
			guide
			showMask
    />
  );
}

const MaskedTextField = forwardRef<any, any>((props, ref) => {
	const { value, onChange } = props;

	return (
		<TextField
			{...props}
			ref={ref}
			InputProps={{
				inputComponent: TextMask,
				value,
				onChange,
			}}
		/>
	);
})

const PhoneNumberTextField: React.SFC<PhoneNumberTextFieldProps> = forwardRef((props, ref) => {
	const {
		appearance,
		value,
		fullWidth,
		enableOnChangeValidation,
		onChange,
		...rest
	} = props;
	const isMaskEnabled = appearance === 'mobile-mask';
	const handleChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
		if (isMaskEnabled) {
			const value = e.currentTarget.value.replace(/\s|\_/g, '');
			const isEmpty = !value || value.length === 6;
			const isCompleted = value.length === 16;
	
			if (isEmpty) {
				onChange(e, '');
			} else if (isCompleted) {
				onChange(e, value);
			}
		} else {
			onChange(e, value);
		}
	};

	const renderMasked = () => {
		return (
			<Root fullWidth={fullWidth}>
				<MaskedTextField
					label='Номер телефона'
					{...rest}
					ref={ref}
					value={value}
					fullWidth
					onChange={handleChange}
				/>
			</Root>
		);
	};

	if (isMaskEnabled) {
		return renderMasked();
	}

	const isValid = enableOnChangeValidation && value
		? detectIsPhone(value.toString())
		: true;
	const hasError = !isValid;

	return (
		<TextField
			ref={ref as React.Ref<any>}
			value={value}
			label='Номер телефона'
			placeholder='Например, +7 (999) 000-00-00'
			error={hasError}
			helperText={hasError ? 'Некорректный номер телефона' : ''}
			fullWidth={fullWidth}
			onChange={onChange}
			{...rest}
		/>
	)
});

PhoneNumberTextField.displayName = 'PhoneNumberTextField';
PhoneNumberTextField.defaultProps = {
	appearance: 'default',
};

const FormPhoneNumberTextFieldBase = withFormTextField<Omit<React.ComponentProps<typeof PhoneNumberTextField>, 'name'>, any>(PhoneNumberTextField);
const FormPhoneNumberTextField: React.FC<React.ComponentProps<typeof FormPhoneNumberTextFieldBase>> = props => {
	const { name } = props;
	const validationRules: Array<ValidationRule> = useMemo(() => ([{
		errorText: 'Некорректный номер телефона',
		fn: (formObject) => {
			const value = getValueByName(name, formObject);
			const isValid = value
				? detectIsPhone(value.toString())
				: true;

			return isValid;
		},
	}]), []);

	return (
		<FormPhoneNumberTextFieldBase
			{...props}
			validationRules={validationRules}
		/>
	);
};

export { FormPhoneNumberTextField };
export default PhoneNumberTextField;
